/**
 * Decide se o polling da SERP deve parar depois de uma falha do
 * `createSearchIntent`. Sem isso o loop repete a mesma chamada até o limite de
 * polls (10 a 40) mesmo quando repetir não adianta, o que gera carga e eventos
 * no Sentry sem chance de sucesso.
 */

/** Falhas seguidas (timeout, 5xx, erro inesperado) toleradas antes de parar, sem nenhuma resposta ainda. */
export const MAX_CONSECUTIVE_POLL_FAILURES = 2;

/**
 * Com resultados já na tela cada poll vale mais e a falha costuma ser passageira
 * (os dados do Sentry de falha repetida são de sessões sem nenhum 200), então
 * tolera mais antes de desistir.
 */
export const MAX_CONSECUTIVE_POLL_FAILURES_AFTER_RESPONSE = 5;

/** 4xx não se resolve repetindo, exceto estes dois, que são transitórios. */
const RETRYABLE_CLIENT_ERRORS = new Set([408, 429]);

type RequestFailureInfo = {
  status?: number;
  code?: string;
  hasResponse: boolean;
};

/** Parte do `AxiosError` que importa aqui, sem importar o axios. */
type AxiosErrorLike = {
  code?: unknown;
  response?: { status?: unknown };
} | null;

/** Lê status e código do erro do axios guardado em `FlightDataRequestError`. */
const getRequestFailureInfo = (error: unknown): RequestFailureInfo => {
  const cause = (
    error instanceof Error && error.cause !== undefined ? error.cause : error
  ) as AxiosErrorLike;
  const status = cause?.response?.status;

  return {
    status: typeof status === "number" ? status : undefined,
    code: typeof cause?.code === "string" ? cause.code : undefined,
    hasResponse: cause?.response !== undefined && cause?.response !== null,
  };
};

type PollFailureContext = {
  error: unknown;
  /** Falhas seguidas até agora, contando esta. */
  consecutiveFailures: number;
  /** Alguma resposta de sucesso já chegou nesta busca. */
  hasReceivedResponse: boolean;
};

export const shouldStopPolling = ({
  error,
  consecutiveFailures,
  hasReceivedResponse,
}: PollFailureContext): boolean => {
  const { status, code, hasResponse } = getRequestFailureInfo(error);

  if (
    status !== undefined &&
    status >= 400 &&
    status < 500 &&
    !RETRYABLE_CLIENT_ERRORS.has(status)
  ) {
    return true;
  }

  // Sem resposta e sem nunca ter funcionado: cliente offline ou bloqueando o
  // domínio da API. Timeout (ECONNABORTED/ETIMEDOUT) não entra aqui porque é
  // lentidão do servidor, que pode melhorar.
  const isNetworkFailure = !hasResponse && code === "ERR_NETWORK";

  if (isNetworkFailure && !hasReceivedResponse) {
    return true;
  }

  const maxFailures = hasReceivedResponse
    ? MAX_CONSECUTIVE_POLL_FAILURES_AFTER_RESPONSE
    : MAX_CONSECUTIVE_POLL_FAILURES;

  return consecutiveFailures >= maxFailures;
};
